/**
 * audioEngine — one shared AudioContext for the whole app.
 *
 * Why this exists (iOS/WebKit — Safari AND Chrome/Edge/Firefox on iOS, which
 * are all WebKit wrappers):
 *
 *  1. WebKit shares a single hardware audio session per page. Creating a fresh
 *     AudioContext for mic metering on every speaking turn — while a second
 *     context holds TTS playback — makes iOS reroute the session and push the
 *     TTS context into the WebKit-only "interrupted" state. Playback then goes
 *     silent with no error, while the mic keeps working.
 *  2. `HTMLAudioElement.play()` deep inside an async fetch chain is rejected by
 *     autoplay policy (NotAllowedError). Web Audio buffer sources on a context
 *     that was unlocked during a real user gesture are exempt — they can start
 *     at any later time without further gestures.
 *  3. Recovery from an OS-level interruption (phone call, Siri, app switch)
 *     requires a user gesture. waitForAudioUnlock() lets playback pause until
 *     any tap resumes the context (a document-level pointerdown handler is
 *     armed while blocked, so any touch recovers — a visible banner provides
 *     discoverability).
 *
 * unlockAudio() MUST be called synchronously from a tap/click handler — the
 * gesture token does not survive an `await`.
 */

let _ctx = null;
let _waiters = [];          // resolvers parked by waitForAudioUnlock()
let _recoveryHandler = null; // document-level pointerdown armed while blocked
let _speechPrimed = false;

export function getAudioCtx() {
  if (!_ctx || _ctx.state === "closed") {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
    // Any transition back to "running" (gesture resume, OS interruption ending)
    // releases everyone parked in waitForAudioUnlock().
    _ctx.onstatechange = () => { if (_ctx.state === "running") flushWaiters(); };
  }
  return _ctx;
}

function flushWaiters() {
  if (_recoveryHandler) {
    document.removeEventListener("pointerdown", _recoveryHandler);
    _recoveryHandler = null;
  }
  _waiters.splice(0).forEach(resolve => resolve());
}

/**
 * Unlock audio for the session. Call synchronously inside a user gesture
 * (tap/click) handler. Safe to call repeatedly.
 */
export function unlockAudio() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state !== "running") {
      ctx.resume().then(() => { if (ctx.state === "running") flushWaiters(); }).catch(() => {});
    }
    // Play a one-sample silent buffer inside the gesture — WebKit only marks
    // the context user-activated once a source has actually started.
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
    // Prime speechSynthesis too (browser-TTS fallback needs its own gesture on iOS).
    if (window.speechSynthesis && !_speechPrimed) {
      _speechPrimed = true;
      try {
        const u = new window.SpeechSynthesisUtterance(" ");
        u.volume = 0;
        window.speechSynthesis.speak(u);
      } catch { /* fallback stays gesture-locked — server TTS is unaffected */ }
    }
  } catch { /* no Web Audio — element playback fallback handles it */ }
}

/**
 * Try to get the context to "running" without a gesture. Returns true on
 * success. Handles both "suspended" and the iOS-only "interrupted" state
 * (ctx.state is checked, not matched by name — resume() covers both).
 */
export async function ensureAudioRunning() {
  const ctx = getAudioCtx();
  if (!ctx) return false;
  if (ctx.state === "running") return true;
  try { await ctx.resume(); } catch { /* needs a gesture — caller waits */ }
  return ctx.state === "running";
}

/**
 * Park until the context reaches "running" (via any tap — a document-level
 * pointerdown handler is armed) or the given AbortSignal fires.
 */
export function waitForAudioUnlock(signal) {
  const ctx = getAudioCtx();
  if (!ctx || ctx.state === "running") return Promise.resolve();
  if (!_recoveryHandler) {
    _recoveryHandler = () => unlockAudio();
    document.addEventListener("pointerdown", _recoveryHandler, { passive: true });
  }
  return new Promise(resolve => {
    _waiters.push(resolve);
    signal?.addEventListener("abort", resolve, { once: true });
  });
}
